import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Alert } from '@mui/material';
import { Print, WhatsApp } from '@mui/icons-material';
import api from '../../services/api';

const KARAT_ORDER = ['24K', '24/24K', '22/22K', '22K', '18K', 'PT95', 'WG'];

// Shared printable voucher layout used by both karigar and shop
export const WorkIssueVoucher = ({ karigar, items, karatParam }) => {
  // Group by karat first, then by issueDate+billName+billNumber as voucher grouping
  const filtered = karatParam ? items.filter(i => i.karat === karatParam) : items;

  // Group by karat
  const byKarat = {};
  filtered.forEach(item => {
    const k = item.karat || 'N/A';
    if (!byKarat[k]) byKarat[k] = [];
    byKarat[k].push(item);
  });

  const sortedKarats = Object.keys(byKarat).sort((a, b) => {
    const ai = KARAT_ORDER.indexOf(a);
    const bi = KARAT_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Derive voucher info from items (use first item's issueDate, billName as voucher ref)
  const voucherNo = items[0]?.voucherNumber || items[0]?.issueVoucherNumber || `${karigar?.abbreviation || ''}-${Date.now()}`;
  const issueDate = items[0]?.issueDate || '';

  const cellSx = { fontSize: '0.72rem', p: '3px 5px', borderColor: '#ccc' };
  const headSx = { fontWeight: 700, fontSize: '0.72rem', p: '4px 5px', borderColor: '#999', backgroundColor: '#f0f0f0' };

  return (
    <Box sx={{
      width: '210mm',
      minHeight: '297mm',
      margin: '0 auto',
      padding: '8mm 10mm',
      backgroundColor: 'white',
      fontFamily: 'Arial, sans-serif',
      '@media print': { margin: 0, padding: '6mm 8mm' }
    }}>
      {/* Top header: company name left, voucher info right */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, borderBottom: '2px solid #333', pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1 }}>WORK ISSUE VOUCHER</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <strong>Karigar:</strong> {karigar?.abbreviation} — {karigar?.name}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <strong>Voucher No:</strong> {voucherNo}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <strong>Issue Date:</strong> {issueDate || '—'}
          </Typography>
        </Box>
      </Box>

      {/* One section per karat */}
      {sortedKarats.map(karat => {
        const karatItems = byKarat[karat];
        const totalWt = karatItems.reduce((s, i) => s + (parseFloat(i.itemWeight) || 0), 0);
        const totalGsb = karatItems.reduce((s, i) => s + (parseFloat(i.gsb || i.gsbWeight) || 0), 0);
        const totalNet = karatItems.reduce((s, i) => {
          const wt = parseFloat(i.itemWeight) || 0;
          const gsb = parseFloat(i.gsb || i.gsbWeight) || 0;
          return s + (wt - gsb);
        }, 0);
        const totalMaking = karatItems.reduce((s, i) => s + (parseFloat(i.makingCharge) || 0), 0);

        return (
          <Box key={karat} sx={{ mb: 3, pageBreakInside: 'avoid' }}>
            {/* Karat header bar */}
            <Box sx={{ backgroundColor: '#1F497D', color: 'white', px: 1.5, py: 0.5, mb: 0.5, display: 'inline-block', borderRadius: '4px' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{karat}</Typography>
            </Box>

            <TableContainer>
              <Table size="small" sx={{ border: '1px solid #ccc', borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headSx} align="center">S.N.</TableCell>
                    <TableCell sx={headSx}>Order No</TableCell>
                    <TableCell sx={headSx}>Priority</TableCell>
                    <TableCell sx={headSx}>Req. Date</TableCell>
                    <TableCell sx={headSx}>Employee</TableCell>
                    <TableCell sx={headSx}>Item Name</TableCell>
                    <TableCell sx={headSx} align="center">Qty</TableCell>
                    <TableCell sx={headSx} align="center">Size</TableCell>
                    <TableCell sx={headSx}>Design / Desc</TableCell>
                    <TableCell sx={headSx} align="right">Wt (g)</TableCell>
                    <TableCell sx={headSx} align="right">GSB</TableCell>
                    <TableCell sx={headSx} align="right">Net (g)</TableCell>
                    <TableCell sx={headSx} align="right">Wastage%</TableCell>
                    <TableCell sx={headSx} align="right">Making</TableCell>
                    <TableCell sx={headSx} align="center">Design Sign</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {karatItems.map((item, idx) => {
                    const wt = parseFloat(item.itemWeight) || 0;
                    const gsb = parseFloat(item.gsb || item.gsbWeight) || 0;
                    const net = wt - gsb;
                    return (
                      <TableRow key={idx} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                        <TableCell sx={cellSx} align="center">{idx + 1}</TableCell>
                        <TableCell sx={cellSx}>{item.billName || ''}{item.billNumber || ''}</TableCell>
                        <TableCell sx={cellSx}>{item.isUrgent ? 'Urgent' : 'Normal'}</TableCell>
                        <TableCell sx={cellSx}>{item.requiredDate || '—'}</TableCell>
                        <TableCell sx={cellSx}>{item.agentName || item.salesman || '—'}</TableCell>
                        <TableCell sx={cellSx}>{item.itemName || '—'}</TableCell>
                        <TableCell sx={cellSx} align="center">{item.quantity || 1} {item.unit || 'Pcs'}</TableCell>
                        <TableCell sx={cellSx} align="center">{item.size || '—'}</TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 120 }}>{item.description || '—'}</TableCell>
                        <TableCell sx={cellSx} align="right">{wt.toFixed(3)}</TableCell>
                        <TableCell sx={cellSx} align="right">{gsb.toFixed(3)}</TableCell>
                        <TableCell sx={cellSx} align="right">{net.toFixed(3)}</TableCell>
                        <TableCell sx={cellSx} align="right">{item.wastage || '—'}</TableCell>
                        <TableCell sx={cellSx} align="right">{(parseFloat(item.makingCharge) || 0).toFixed(2)}</TableCell>
                        <TableCell sx={cellSx} align="center"></TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  <TableRow sx={{ backgroundColor: '#e8e8e8' }}>
                    <TableCell colSpan={9} sx={{ ...headSx, textAlign: 'right' }}>Total ({karat}):</TableCell>
                    <TableCell sx={headSx} align="right">{totalWt.toFixed(3)}</TableCell>
                    <TableCell sx={headSx} align="right">{totalGsb.toFixed(3)}</TableCell>
                    <TableCell sx={headSx} align="right">{totalNet.toFixed(3)}</TableCell>
                    <TableCell sx={headSx}></TableCell>
                    <TableCell sx={headSx} align="right">{totalMaking.toFixed(2)}</TableCell>
                    <TableCell sx={headSx}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {/* Grand total if multiple karats */}
      {sortedKarats.length > 1 && (
        <Box sx={{ mt: 1, borderTop: '2px solid #333', pt: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            Grand Total Weight: {filtered.reduce((s, i) => s + (parseFloat(i.itemWeight) || 0), 0).toFixed(3)} g &nbsp;|&nbsp;
            Net: {filtered.reduce((s, i) => s + ((parseFloat(i.itemWeight) || 0) - (parseFloat(i.gsb || i.gsbWeight) || 0)), 0).toFixed(3)} g
          </Typography>
        </Box>
      )}

      {/* Signature row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #ccc' }}>
        <Box sx={{ textAlign: 'center', width: '30%' }}>
          <Box sx={{ borderTop: '1px solid #333', mt: 4, pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem' }}>Prepared By</Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center', width: '30%' }}>
          <Box sx={{ borderTop: '1px solid #333', mt: 4, pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem' }}>Checked By</Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center', width: '30%' }}>
          <Box sx={{ borderTop: '1px solid #333', mt: 4, pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem' }}>Karigar Signature</Typography>
          </Box>
        </Box>
      </Box>

      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">No work issued items found</Typography>
        </Box>
      )}
    </Box>
  );
};

const WorkIssueList = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const karigarParam = params.get('karigar') || '';
  const karatParam = params.get('karat') || '';

  const [karigar, setKarigar] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!karigarParam) { setError('Karigar parameter is required'); setLoading(false); return; }
      try {
        const [karigarRes, itemsRes] = await Promise.all([
          api.get('/api/karigar'),
          api.get(`/api/karigar/work-issued?karigar=${karigarParam}`)
        ]);
        setKarigar(karigarRes.data.find(k => k.abbreviation === karigarParam) || null);
        setItems(itemsRes.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [karigarParam]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

  const handleWhatsApp = () => {
    const phone = karigar?.phone?.replace(/\D/g, ''); // strip non-digits
    if (!phone) { alert('No phone number found for this karigar.'); return; }
    const totalItems = items.length;
    const totalWeight = items.reduce((s, i) => s + (parseFloat(i.itemWeight) || 0), 0).toFixed(3);
    const karats = [...new Set(items.map(i => i.karat))].join(', ');
    const msg = `Work Issue Voucher\nKarigar: ${karigar?.abbreviation} - ${karigar?.name}\nItems: ${totalItems}\nKarat: ${karats}\nTotal Weight: ${totalWeight}g\nPlease check and sign the voucher.`;
    // Normalize Nepal numbers: add country code 977 if starts with 9 and is 10 digits
    const normalized = phone.length === 10 && phone.startsWith('9') ? `977${phone}` : phone;
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Box>
      <Box className="no-print" sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="contained" color="success" startIcon={<WhatsApp />} onClick={handleWhatsApp}
          disabled={!karigar?.phone}>
          Share via WhatsApp
        </Button>
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
      </Box>
      <WorkIssueVoucher karigar={karigar} items={items} karatParam={karatParam} />
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 8mm; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </Box>
  );
};

export default WorkIssueList;
